<template>
  <div class="scp-panel">
    <div ref="containerRef" class="scp-canvas" :style="canvasStyle">
      <img
        v-if="bgImage"
        :src="bgImage"
        class="scp-bg"
        draggable="false"
        @load="onBgLoad"
        @mousedown.prevent.stop
      />
      <div
        v-if="fgImage"
        class="scp-fg-wrapper"
        :style="fgWrapperStyle"
        @mousedown="onDragStart"
      >
        <img
          :src="fgImage"
          class="scp-fg"
          draggable="false"
          @load="onFgLoad"
        />
        <button
          class="scp-lock-btn"
          :class="{ locked: isAspectLocked }"
          :title="isAspectLocked ? '宽高比已锁定（点击解锁自由变形）' : '宽高比未锁定（点击锁定当前比例）'"
          @click.stop="toggleAspectLock"
          @mousedown.stop.prevent
        >
          <svg v-if="isAspectLocked" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
            <rect x="5" y="11" width="14" height="9" rx="2"/>
            <path d="M8 11V7a4 4 0 0 1 8 0v4"/>
          </svg>
          <svg v-else viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
            <rect x="5" y="11" width="14" height="9" rx="2"/>
            <path d="M8 11V7a4 4 0 0 1 7.5-2"/>
          </svg>
        </button>
        <div class="scp-handle scp-handle-br" title="拖动调整大小" @mousedown.stop.prevent="onResizeStart('br', $event)">
          <svg viewBox="0 0 10 10" width="10" height="10">
            <path d="M9 1 L9 9 L1 9" fill="none" stroke="#fff" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </div>
        <div class="scp-handle scp-handle-bl" title="拖动调整大小" @mousedown.stop.prevent="onResizeStart('bl', $event)"></div>
        <div class="scp-handle scp-handle-tr" title="拖动调整大小" @mousedown.stop.prevent="onResizeStart('tr', $event)"></div>
        <div class="scp-handle scp-handle-tl" title="拖动调整大小" @mousedown.stop.prevent="onResizeStart('tl', $event)"></div>
      </div>
      <div v-if="combining" class="scp-overlay">
        <div class="scp-spinner"></div>
        <span>正在融合...</span>
      </div>
    </div>
    <div class="scp-hint">拖动表格调整位置 · 拖动四角调整大小 · 透明度固定 50%</div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, reactive, computed } from 'vue'
import { useWorkbenchStore } from '@/stores/useWorkbenchStore'

const props = defineProps({
  bgImage: { type: String, default: '' },
  fgImage: { type: String, default: '' },
  productIndex: { type: Number, required: true }
})

const workbench = useWorkbenchStore()
const combineRef = computed(() => workbench.sizeCharts[props.productIndex]?.combine || {
  widthRatio: 0.8,
  posXRatio: 0.1,
  posYRatio: null,
  opacity: 0.5
})

const containerRef = ref(null)
const bgNatural = reactive({ w: 1, h: 1 })
const fgNatural = reactive({ w: 1, h: 1 })

const onBgLoad = (e) => {
  bgNatural.w = e.target.naturalWidth || 1
  bgNatural.h = e.target.naturalHeight || 1
}
const onFgLoad = (e) => {
  fgNatural.w = e.target.naturalWidth || 1
  fgNatural.h = e.target.naturalHeight || 1
}

const canvasStyle = computed(() => {
  const ratio = bgNatural.w > 1 && bgNatural.h > 1 ? `${bgNatural.w} / ${bgNatural.h}` : '4 / 3'
  return { aspectRatio: ratio }
})

const fgAspect = computed(() => (fgNatural.w > 0 && fgNatural.h > 0 ? fgNatural.w / fgNatural.h : 1))

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))

const fgWrapperStyle = computed(() => {
  const c = combineRef.value
  const widthRatio = c.widthRatio ?? 0.8
  const heightRatio = c.heightRatio
  let topPct
  if (c.posYRatio != null) {
    topPct = c.posYRatio * 100
  } else {
    const fgHeightRatioOfBg = heightRatio != null
      ? heightRatio
      : (widthRatio / fgAspect.value) * (bgNatural.w / bgNatural.h)
    topPct = Math.max(0, (1 - fgHeightRatioOfBg - 0.05)) * 100
  }
  const style = {
    left: `${(c.posXRatio ?? (1 - widthRatio) / 2) * 100}%`,
    top: `${topPct}%`,
    width: `${widthRatio * 100}%`,
    opacity: 0.5
  }
  if (heightRatio != null) {
    style.height = `${heightRatio * 100}%`
  } else {
    style.aspectRatio = `${fgAspect.value}`
  }
  return style
})

const materializePosY = () => {
  const c = workbench.sizeCharts[props.productIndex]?.combine
  if (!c || c.posYRatio != null) return
  const fgHeightRatioOfBg =
    ((c.widthRatio ?? 0.8) / fgAspect.value) * (bgNatural.w / bgNatural.h)
  c.posYRatio = clamp(1 - fgHeightRatioOfBg - 0.05, 0, 1)
}

const onDragStart = (e) => {
  if (e.button !== 0) return
  const c = workbench.sizeCharts[props.productIndex]?.combine
  if (!c) return
  e.preventDefault()
  const rect = containerRef.value.getBoundingClientRect()
  const startX = e.clientX
  const startY = e.clientY
  const startPosX = c.posXRatio ?? (1 - c.widthRatio) / 2
  materializePosY()
  const startPosY = c.posYRatio

  const onMove = (ev) => {
    const dx = (ev.clientX - startX) / rect.width
    const dy = (ev.clientY - startY) / rect.height
    c.posXRatio = clamp(startPosX + dx, 0, Math.max(0, 1 - c.widthRatio))
    c.posYRatio = clamp(startPosY + dy, 0, 0.99)
  }
  const onUp = () => {
    document.removeEventListener('mousemove', onMove)
    document.removeEventListener('mouseup', onUp)
  }
  document.addEventListener('mousemove', onMove)
  document.addEventListener('mouseup', onUp)
}

// 四角缩放：以对角点为锚点；锁定模式下 width 决定一切（height 等比），解锁后 width/height 独立
const onResizeStart = (corner, e) => {
  if (e.button !== 0) return
  const c = workbench.sizeCharts[props.productIndex]?.combine
  if (!c) return
  e.preventDefault()
  const rect = containerRef.value.getBoundingClientRect()
  const startW = c.widthRatio ?? 0.8
  const startXPos = c.posXRatio ?? (1 - startW) / 2
  materializePosY()
  const startYPos = c.posYRatio
  const startH = c.heightRatio ?? currentFgHeightRatio()
  const locked = c.aspectLocked !== false

  // 锚点（被拖角的对角点）位置
  const anchorIsLeft = corner === 'br' || corner === 'tr'
  const anchorIsTop = corner === 'br' || corner === 'bl'
  const anchorX = anchorIsLeft ? startXPos : startXPos + startW
  const anchorY = anchorIsTop ? startYPos : startYPos + startH

  const onMove = (ev) => {
    const pointerX = (ev.clientX - rect.left) / rect.width
    const pointerY = (ev.clientY - rect.top) / rect.height

    const newW = clamp(Math.abs(pointerX - anchorX), 0.2, 1)
    c.widthRatio = newW
    c.posXRatio = clamp(Math.min(pointerX, anchorX), 0, 1 - newW)

    if (locked) {
      if (c.heightRatio != null) {
        // 已变形过：按当前变形比例同步缩放，保持锚点不动
        const aspect = startW / startH
        const newH = clamp(newW / aspect, 0.05, 1)
        c.heightRatio = newH
        c.posYRatio = clamp(anchorIsTop ? anchorY : anchorY - newH, 0, 0.99)
      }
      // 否则 heightRatio=null（默认原图等比），CSS 用 aspect-ratio 自动算 height，posY 不变
    } else {
      const newH = clamp(Math.abs(pointerY - anchorY), 0.05, 1)
      c.heightRatio = newH
      c.posYRatio = clamp(Math.min(pointerY, anchorY), 0, 0.99)
    }
  }
  const onUp = () => {
    document.removeEventListener('mousemove', onMove)
    document.removeEventListener('mouseup', onUp)
  }
  document.addEventListener('mousemove', onMove)
  document.addEventListener('mouseup', onUp)
}

const combining = computed(() => workbench.sizeCharts[props.productIndex]?.combining)

const isAspectLocked = computed(
  () => workbench.sizeCharts[props.productIndex]?.combine?.aspectLocked !== false
)

const currentFgHeightRatio = () => {
  const c = workbench.sizeCharts[props.productIndex]?.combine
  if (!c) return 0
  const widthRatio = c.widthRatio ?? 0.8
  return (widthRatio / fgAspect.value) * (bgNatural.w / bgNatural.h)
}

const toggleAspectLock = () => {
  const c = workbench.sizeCharts[props.productIndex]?.combine
  if (!c) return
  if (c.aspectLocked === false) {
    // 解锁 → 锁定：保持当前变形后的比例（不清空 heightRatio）
    c.aspectLocked = true
  } else {
    // 锁定 → 解锁：把当前等比 height 显式化，便于自由变形
    c.aspectLocked = false
    if (c.heightRatio == null) {
      c.heightRatio = clamp(currentFgHeightRatio(), 0.05, 1)
    }
  }
}
</script>

<style scoped>
.scp-panel {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.scp-canvas {
  position: relative;
  width: 100%;
  background: var(--surface-soft);
  border-radius: 8px;
  overflow: hidden;
  user-select: none;
  box-shadow: inset 0 0 0 1px var(--border-color);
}
.scp-lock-btn {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 5;
  width: 22px;
  height: 22px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(217, 138, 82, 0.35);
  border-radius: 5px;
  color: var(--text-sub);
  cursor: pointer;
  transition: color 0.15s, background 0.15s, border-color 0.15s;
}
.scp-lock-btn:hover {
  background: #fff;
}
.scp-lock-btn.locked {
  color: var(--primary-active);
  border-color: var(--primary-color);
  background: rgba(249, 239, 227, 0.95);
}
.scp-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  pointer-events: none;
}
.scp-fg-wrapper {
  position: absolute;
  cursor: move;
  border: 1.5px dashed rgba(217, 138, 82, 0.7);
  box-sizing: border-box;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.4);
}
.scp-fg {
  display: block;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
.scp-handle {
  position: absolute;
  width: 14px;
  height: 14px;
  background: var(--primary-color);
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
}
.scp-handle-br {
  right: -7px;
  bottom: -7px;
  cursor: nwse-resize;
  display: flex;
  align-items: center;
  justify-content: center;
}
.scp-handle-bl {
  left: -7px;
  bottom: -7px;
  cursor: nesw-resize;
}
.scp-handle-tr {
  right: -7px;
  top: -7px;
  cursor: nesw-resize;
}
.scp-handle-tl {
  left: -7px;
  top: -7px;
  cursor: nwse-resize;
  display: flex;
  align-items: center;
  justify-content: center;
}
.scp-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--text-body);
  font-size: 13px;
  z-index: 10;
}
.scp-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid var(--border-strong);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: scp-spin 0.8s linear infinite;
}
@keyframes scp-spin {
  to { transform: rotate(360deg); }
}
.scp-hint {
  font-size: 12px;
  color: var(--text-sub);
  text-align: center;
}
</style>
